import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { CalendarDatePicker } from './CalendarDatePicker';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  editable?: boolean;
  minimumDate?: Date;
  maximumDate?: Date;
};

/**
 * Calendar date field for filter/report screens.
 * Value stays YYYY-MM-DD; users pick from the calendar (no manual typing).
 */
export function AppDateField({
  label,
  value,
  onChange,
  placeholder = 'Select date',
  error,
  editable = true,
  minimumDate,
  maximumDate,
}: Props) {
  const palette = useAppColors();
  const [open, setOpen] = useState(false);
  const displayValue = value.trim();

  return (
    <View style={styles.container}>
      <Text style={[styles.label, { color: palette.textSecondary }]}>
        {label}
      </Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityHint={
          displayValue
            ? `Selected ${displayValue}`
            : 'Opens calendar date picker'
        }
        accessibilityState={{ disabled: !editable }}
        disabled={!editable}
        onPress={() => {
          if (editable) setOpen(true);
        }}
        style={[
          styles.field,
          {
            borderColor: error ? palette.error : palette.border,
            backgroundColor: palette.surface,
          },
        ]}
      >
        <Text
          style={[
            styles.value,
            {
              color: displayValue ? palette.textPrimary : palette.textMuted,
            },
          ]}
        >
          {displayValue || placeholder}
        </Text>
        <Text style={[styles.calendarGlyph, { color: palette.textMuted }]}>
          ▦
        </Text>
      </Pressable>
      {error ? (
        <Text
          accessibilityRole="alert"
          style={[styles.error, { color: palette.error }]}
        >
          {error}
        </Text>
      ) : null}
      <CalendarDatePicker
        open={open}
        value={value}
        minimumDate={minimumDate}
        maximumDate={maximumDate}
        onClose={() => setOpen(false)}
        onConfirm={next => {
          onChange(next);
          setOpen(false);
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  label: {
    ...typography.caption,
    fontWeight: '600',
  },
  field: {
    minHeight: 50,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  value: {
    ...typography.input,
    flex: 1,
    paddingVertical: spacing.md,
  },
  calendarGlyph: {
    fontSize: 16,
    fontWeight: '600',
  },
  error: { ...typography.caption },
});
